import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Filter, Clock, Calendar, Star, Play } from 'lucide-react';

const SearchModal = ({ isOpen, onClose, allMovies, onPlayVideo }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGenre, setSelectedGenre] = useState('all');
    const [selectedYear, setSelectedYear] = useState('all');
    const [selectedRating, setSelectedRating] = useState('all');
    const inputRef = useRef(null);

    // Focus en input cuando se abre
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current.focus(), 300);
        }
    }, [isOpen]);

    // Obtener géneros únicos
    const genres = ['all', ...new Set(allMovies.flatMap(movie => movie.genres || []))];

    // Obtener años únicos
    const years = ['all', ...new Set(allMovies.map(movie => movie.year).filter(Boolean).sort((a, b) => b - a))];

    // Filtrar películas
    const filteredMovies = allMovies.filter(movie => {
        const matchesSearch = movie.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            movie.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesGenre = selectedGenre === 'all' || movie.genres?.includes(selectedGenre);
        const matchesYear = selectedYear === 'all' || movie.year == selectedYear;
        const matchesRating = selectedRating === 'all' || movie.rating >= parseFloat(selectedRating);

        return matchesSearch && matchesGenre && matchesYear && matchesRating;
    });

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedGenre('all');
        setSelectedYear('all');
        setSelectedRating('all');
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm"
                >
                    <div className="container mx-auto px-4 py-6 h-full flex flex-col">
                        {/* Header */}
                        <div className="flex items-center gap-4 mb-6">
                            <div className="relative flex-1">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    placeholder="Buscar películas, series, documentales..."
                                    className="w-full pl-12 pr-4 py-4 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-red-500 text-lg"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <motion.button
                                onClick={onClose}
                                className="p-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-gray-400 hover:text-white transition-colors"
                                whileHover={{ scale: 1.05 }}
                            >
                                <X className="w-6 h-6" />
                            </motion.button>
                        </div>

                        {/* Filtros */}
                        <div className="flex gap-4 mb-6 overflow-x-auto pb-2">
                            <select
                                value={selectedGenre}
                                onChange={(e) => setSelectedGenre(e.target.value)}
                                className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-500"
                            >
                                <option value="all">Todos los géneros</option>
                                {genres.filter(g => g !== 'all').map(genre => (
                                    <option key={genre} value={genre}>{genre}</option>
                                ))}
                            </select>

                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                                className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-500"
                            >
                                <option value="all">Todos los años</option>
                                {years.filter(y => y !== 'all').map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>

                            <select
                                value={selectedRating}
                                onChange={(e) => setSelectedRating(e.target.value)}
                                className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-500"
                            >
                                <option value="all">Cualquier rating</option>
                                <option value="8">8+ Estrellas</option>
                                <option value="7">7+ Estrellas</option>
                                <option value="6">6+ Estrellas</option>
                            </select>

                            {(searchTerm || selectedGenre !== 'all' || selectedYear !== 'all' || selectedRating !== 'all') && (
                                <motion.button
                                    onClick={clearFilters}
                                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors whitespace-nowrap"
                                    whileHover={{ scale: 1.05 }}
                                >
                                    Limpiar filtros
                                </motion.button>
                            )}
                        </div>

                        {/* Resultados */}
                        <div className="flex-1 overflow-y-auto">
                            {filteredMovies.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredMovies.map(movie => (
                                        <motion.div
                                            key={movie.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="bg-gray-800/50 rounded-xl overflow-hidden border border-gray-700/50 hover:border-gray-600/70 transition-all duration-300 group"
                                        >
                                            <div className="flex h-32">
                                                <img
                                                    src={movie.image}
                                                    alt={movie.title}
                                                    className="w-24 h-full object-cover flex-shrink-0"
                                                />
                                                <div className="flex-1 p-4">
                                                    <h3 className="text-white font-semibold line-clamp-1 group-hover:text-red-400 transition-colors">
                                                        {movie.title}
                                                    </h3>
                                                    <div className="flex items-center gap-2 mt-1 text-gray-400 text-sm">
                                                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                                        <span>{movie.rating}</span>
                                                        <span>•</span>
                                                        <Calendar className="w-3 h-3" />
                                                        <span>{movie.year}</span>
                                                    </div>
                                                    <p className="text-gray-300 text-sm line-clamp-2 mt-2">
                                                        {movie.description}
                                                    </p>
                                                    <motion.button
                                                        onClick={() => onPlayVideo(movie)}
                                                        className="mt-3 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm flex items-center gap-1 transition-colors"
                                                        whileHover={{ scale: 1.05 }}
                                                    >
                                                        <Play className="w-3 h-3" />
                                                        Reproducir
                                                    </motion.button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                                    <h3 className="text-2xl font-bold text-white mb-2">No se encontraron resultados</h3>
                                    <p className="text-gray-400">Intenta con otros términos de búsqueda o ajusta los filtros</p>
                                </div>
                            )}
                        </div>

                        {/* Contador de resultados */}
                        <div className="mt-4 pt-4 border-t border-gray-700/50">
                            <p className="text-gray-400 text-sm">
                                {filteredMovies.length} {filteredMovies.length === 1 ? 'resultado' : 'resultados'} encontrados
                            </p>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default SearchModal;